/**
 * Server functions for feature flag evaluation.
 *
 * Provides authenticated flag evaluation with database-backed
 * configuration fetching and caching.
 */

import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { z } from "zod"
import { assertPermission, type AuthenticatedUser } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"
import type { EvaluationContext, EvaluationResult } from "../types/flagTypes"
import { invalidateFlagCache } from "../core/cache"
import { evaluateFlagForUser } from "./evaluateFlag.server"

// =============================================================================
// Input validation schemas
// =============================================================================

const evaluateFlagInputSchema = z.object({
  /** Flag key to evaluate */
  flagKey: z.string().min(1),
  /** Default value if flag not found */
  defaultValue: z.union([z.boolean(), z.string(), z.number()]),
  /** Optional context overrides (userId, role, etc. come from session by default) */
  context: z
    .object({
      countryId: z.string().optional(),
      storeId: z.string().optional(),
    })
    .optional(),
})

export type EvaluateFlagInput = z.infer<typeof evaluateFlagInputSchema>

export const evaluateFlagFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator((input: unknown) => evaluateFlagInputSchema.parse(input))
  .handler(async ({ data, context }): Promise<EvaluationResult> => {
    // Use user from middleware context (already authenticated)
    const user = context.user as unknown as AuthenticatedUser
    return evaluateFlagForUser(data.flagKey, data.defaultValue, user, data.context)
  })

/**
 * Invalidate the cache for a specific flag.
 * Called after flag mutations to ensure fresh evaluation.
 *
 * Admin-only operation.
 */
export const invalidateFlagCacheFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator((input: unknown) =>
    z
      .object({
        flagId: z.string().min(1),
        environment: z.enum(["dev", "staging", "production"]).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const request = getRequest()
    // Assert admin permission for cache invalidation
    await assertPermission(request, "featureFlag", "update")

    const { flagId, environment } = data
    invalidateFlagCache(flagId, environment)

    return { success: true }
  })

// Re-export types for consumers
export type { EvaluationResult, EvaluationContext }
